<template>
	<div class="widget-selector-modal" :class="{ 'tile-editor': isTileEditor }">
		<div class="search-section flex-centerize px-4">
			<AppSearch tabindex="0" ref="search_field_ref" class="mr-2" :searchString.sync="searchString" placeholder="Search Blocks" />
			<AppTooltip text="Learn more about interface blocks">
				<DocsDispatcher docsSrc="//docs.ply.io/build-features/interfaces/prefilling-interface-blocks">
					<IconButton tabindex="-1">
						<ExternalLinkSvg />
					</IconButton>
				</DocsDispatcher>
			</AppTooltip>
		</div>
		<div class="section-container" v-for="(fieldGroupObj, index) in widgetFields" :key="index">
			<div class="section-title">{{ fieldGroupObj.separatorText }}</div>
			<div class="widget-field-section">
				<button
					v-for="(field, i) in fieldGroupObj.fieldGroup"
					:key="i"
					@click="addField(field)"
					class="widget-field flex-centerize flex-column"
				>
					<MaybeUpgradeRequiredIndicator :billingFeatureId="field.billingFeatureId" v-if="field.billingFeatureId">
						<template #default="{ indicator, isUpgradeRequired }">
							<div class="pl-14" v-if="field.billingFeatureId && isUpgradeRequired && isCustomerFeature">
								<component :is="indicator" :billingFeatureId="field.billingFeatureId" iconOnly canBuild />
							</div>
						</template>
					</MaybeUpgradeRequiredIndicator>
					<div class="icon-container">
						<component class="field-icon" :is="field.svgIcon" />
						<component class="field-icon-hover" :is="field.svgHoverIcon" />
					</div>
					<div class="mt-3">{{ field.name }}</div>
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import ExternalLinkSvg from './assets/ExternalLinkSvg';
import WidgetSelectorModalService from './widget-selector-modal-service';
import MaybeUpgradeRequiredIndicator from '@/components/Billing/MaybeUpgradeRequiredIndicator.vue';
import DocsDispatcher from '@/components/common/DocsDispatcher';
import { isRichTextSupportOn, isAIMiniAppSupportOn, isCascadeDropdownEnabled } from '@/store/modules/featureFlags/getters';
import { isEndRunWidget } from '@/store/modules/selectedNode/getters';
import { editWorkflow } from '@/store/modules/selectedWorkflow/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import WorkflowService from '@/utils/WorkflowService';
import WorkflowToolService from '@/utils/workflow-tool-service';
export default {
	name: 'WidgetSelectorModal',
	components: { DocsDispatcher, ExternalLinkSvg, MaybeUpgradeRequiredIndicator },
	props: {
		isOpen: { type: Boolean },
		isTileEditor: { type: Boolean },
		showConfigurationOnlyFields: { type: Boolean, default: false },
	},

	computed: {
		...mapGettersShorthand({ isRichTextSupportOn, isAIMiniAppSupportOn, isCascadeDropdownEnabled, isEndRunWidget, editWorkflow }),
		widgetFields() {
			const { searchString, isRichTextSupportOn, isAIMiniAppSupportOn, showConfigurationOnlyFields, isCascadeDropdownEnabled } = this;
			const isSlackTool = WorkflowToolService.isSlackTool(this.editWorkflow);
			return WidgetSelectorModalService.getFieldGroups(
				searchString,
				isRichTextSupportOn,
				isAIMiniAppSupportOn,
				showConfigurationOnlyFields,
				isCascadeDropdownEnabled,
				this.isEndRunWidget,
				this.isTileEditor,
				isSlackTool
			);
		},
		isCustomerFeature() {
			return WorkflowService.isCustomerFacingFeature(this.editWorkflow);
		},
	},
	methods: {
		addField(field) {
			this.$emit('addNewField', field);
			this.searchString = '';
		},
		focusOnSearchInputElement() {
			setTimeout(() => {
				this.$refs.search_field_ref.$el.getElementsByTagName('input')[0].focus();
			}, 100);
		},
	},
	mounted() {
		this.focusOnSearchInputElement();
	},
	watch: {
		isOpen: {
			handler(newV) {
				if (newV) {
					this.focusOnSearchInputElement();
				}
			},
		},
	},
	data() {
		return { searchString: '' };
	},
};
</script>

<style scoped lang="scss">
@import '@/styles/mixins/service-icon-mixin.scss';

.widget-selector-modal {
	max-height: 60vh;
	width: var(--widget-selector-modal-width);

	&.tile-editor {
		width: var(--tile-widget-selector-modal-width);
	}

	border-radius: 10px;
	color: #354052;
	background-color: var(--bg-color);

	.search-section {
		background-color: var(--bg-color);
		border-bottom: 1px solid #e4e8f2;

		::v-deep(.v-text-field--outlined:not(.v-textarea) > .v-input__control > .v-input__slot fieldset) {
			border-radius: 9px !important;
		}
		::v-deep(button) {
			height: 32px !important;
			width: 32px !important;
			border-radius: 9px !important;
		}
	}

	.section-container {
		background-color: var(--bg-color);

		.section-title {
			width: 100%;
			margin: 0 0 12px 0;
			padding: 10px;
			text-transform: uppercase;
			font-weight: 600;
			font-size: 9px;
			color: #7a8694;
			background-color: #f9fafd;
		}
		.widget-field-section {
			display: flex;
			flex-wrap: wrap;
			justify-content: space-between;
			padding: 0 12px 0 12px;

			&:after {
				content: '';
				flex: auto;
			}
			.widget-field {
				height: 100px;
				width: 100px;
				margin: 0 12px 12px 0;
				border: 1px solid #e4e8f2;
				border-radius: 9px;
				text-align: center;
				font-size: 12px;
			}
			@include service-icon-mixin('widget-field');
		}
	}
	@keyframes fdsseq {
		100% {
			opacity: 1;
		}
	}
	.widget-field {
		animation: fdsseq 0.5s forwards;
	}

	@for $i from 1 through 10 {
		.widget-field:nth-child(#{$i}) {
			animation-delay: #{$i * 0.05}s;
		}
	}
}
</style>
